// src/components/AppComponents/Sidebar/CustomerMenu.tsx

import { useRef } from 'react';
import {
  RiBuildingLine,
  RiCircleFill,
  RiExpandUpDownLine,
} from '@remixicon/react';
import { useUserCustomersUseCase } from '@/domains/user/user.usecases';
import { useUserCustomersStore } from '@/domains/user/userCustomers.store';
import { useUserSettingsStore } from '@/domains/user/userSettings.store';
import { useUserProfileStore } from '@/domains/user/userProfile.store';
import { TCustomerKycStatus, TCustomerStatus } from '@/shared/types/customer';
import i18n from '@/shared/i18n/i18n';
import { USER_ROLES } from '@/shared/types/user';
import { cx } from '@UI/lib/utils';
import { Loader } from '@UI/components/Loader';
import { Icon } from '@UI/components/Icon';
import {
  Menu,
  MenuButton,
  MenuHeading,
  MenuItem,
  MenuItems,
  MenuSection,
} from '@UI/components/Menu';
import CustomerMenuItemButton from './CustomerMenu/CusomerMenuItemButton';
import CustomerMenuItem from './CustomerMenu/CusomerMenuItem';
import CustomerLoadErrorDialog from './CustomerMenu/CustomerLoadErrorDialog';

const getColor = (status: TCustomerStatus, kyc_status: TCustomerKycStatus) => {
  if (status === 'demo') return 'indigo';
  if (status === 'live' && kyc_status === 'passed') return 'green';
  if (status !== 'live' && kyc_status !== 'passed') return 'red';
  return 'yellow';
};

const CustomerMenu: React.FC = () => {
  const isCollapsed = useUserSettingsStore(
    (state) => state.settings.menu_collapsed,
  );
  const { user } = useUserProfileStore();
  const { isLoading, isError, refetch } = useUserCustomersUseCase();
  const { customers, selectedCustomer, selectCustomer } =
    useUserCustomersStore();

  const hadErrorRef = useRef(false);

  if (isError && customers.length === 0) {
    hadErrorRef.current = true;
  }
  if (customers.length > 0) {
    hadErrorRef.current = false;
  }

  if (hadErrorRef.current && customers.length === 0) {
    return (
      <CustomerLoadErrorDialog retryAction={refetch} isLoading={isLoading} />
    );
  }
  return (
    <Loader
      loading={isLoading}
      size="xs"
      className="bg-revving-background-muted dark:bg-darkRevving-background-muted"
    >
      {customers.length > 0 ? (
        customers.length > 1 ? (
          <Menu>
            <MenuButton
              as={CustomerMenuItemButton}
              className={cx(isCollapsed ? 'pr-4' : '')}
            >
              <Icon
                icon={RiBuildingLine}
                color="indigo"
                size="md"
                className={cx(!isCollapsed ? '-ml-2.5 mr-1.5' : '')}
              />
              {!isCollapsed && (
                <span className="hidden lg:flex justify-between items-center">
                  <CustomerMenuItem
                    name={selectedCustomer?.name ?? ''}
                    role={selectedCustomer?.role as keyof typeof USER_ROLES}
                    className="w-[calc(var(--expanded-sidebar-width)-6.5rem)]"
                  />
                  <Icon icon={RiExpandUpDownLine} size="md" color="slate" />
                </span>
              )}
            </MenuButton>
            <MenuItems
              // anchor="bottom"
              transition
              anchor={{
                gap: `${isCollapsed ? '1.3rem' : '.5rem'}`,
                to: `${isCollapsed ? 'right' : 'bottom'}`,
              }}
              className={cx(
                'z-50 flex flex-col w-[calc(var(--expanded-sidebar-width)-1rem)] p-2',
                'lg:rounded-xl border border-revving-border bg-revving-background ',
                'dark:border-darkRevving-border dark:bg-darkRevving-background',
                'mobilePanel',
              )}
            >
              <MenuSection className="space-y-2">
                <MenuHeading className="text-xs opacity-50 truncate">
                  {i18n.t('navBar.customerList', { number: customers.length })}
                </MenuHeading>
                {customers.map((customer) => (
                  <MenuItem
                    key={customer.uuid}
                    as={CustomerMenuItemButton}
                    onClick={() => selectCustomer(customer)}
                  >
                    {user?.is_bizops && (
                      <Icon
                        icon={RiCircleFill}
                        color={getColor(customer.status, customer.kyc_status)}
                        size="xs"
                        className={cx(!isCollapsed ? '-ml-2.5 mr-1.5' : '')}
                      />
                    )}
                    <CustomerMenuItem
                      name={customer.name ?? ''}
                      role={customer.role as keyof typeof USER_ROLES}
                      className={
                        user?.is_bizops
                          ? 'w-[calc(var(--expanded-sidebar-width)-5.5rem)]'
                          : 'w-[calc(var(--expanded-sidebar-width)-4.5rem)]'
                      }
                    />
                  </MenuItem>
                ))}
              </MenuSection>
            </MenuItems>
          </Menu>
        ) : (
          <div
            className={cx(
              'shrink-0 inline-flex items-center group outline-none',
              'rounded-revving-default',
              'px-4 py-2',
              'text-sm',
              'bg-transparent dark:bg-transparent',
              'border border-revving-subtle dark:border-darkRevving-subtle',
              'w-full justify-start max-lg:pr-1.5',
            )}
          >
            <Icon icon={RiBuildingLine} color="indigo" size="md" />
            {!isCollapsed && (
              <span className="hidden lg:flex justify-between items-center">
                <CustomerMenuItem
                  name={selectedCustomer?.name ?? ''}
                  role={selectedCustomer?.role as keyof typeof USER_ROLES}
                  className="w-[calc(var(--expanded-sidebar-width)-4.5rem)]"
                />
              </span>
            )}
          </div>
        )
      ) : null}
    </Loader>
  );
};

export default CustomerMenu;
